import { TournamentCard } from '../../../components/common/cards';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useRoleContext } from '../../../context/RoleContext';
import { fetchTournaments } from '../../../services/tournamentService';
import { getMatchManagerDashboardPath, getOrganizationId } from '../../../utils/organization';
import { getClubId, getMatchManagerDashboardPath as getClubMatchManagerDashboardPath } from '../../../utils/club';
import PageShell from '../../../components/layout/PageShell';
import {
  getCommunityId,
  getMatchManagerDashboardPath as getCommunityMatchManagerDashboardPath,
} from '../../../utils/community';
import {
  buildManagementScope,
  buildMatchManagerFormatsPath,
  getKnownTournamentId,
} from '../../../utils/managementNavigation';
import { normalizeId } from '../../../utils/ids';

const getTournamentId = (tournament) =>
  tournament?.id ||
  tournament?._id?.toString?.() ||
  tournament?.tournamentId ||
  tournament?._id ||
  '';

const storedTournamentKey = 'matchManagerTournamentId';

const MatchManagerTournamentsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    organizationId: routeOrganizationId,
    communityId: routeCommunityId,
    clubId: routeClubId,
    managerId: routeManagerId,
  } = useParams();
  const { token, user } = useAuth();
  const { activeContext } = useRoleContext();
  const [tournaments, setTournaments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const userRoles = useMemo(() => new Set([...(user?.roles || []), user?.role].filter(Boolean)), [user]);
  const isOrganizationAdmin = userRoles.has('organization_admin');
  const isClubAdmin = userRoles.has('club_admin');
  const isTournamentAdmin = userRoles.has('tournament_admin');
  const isMatchManager = userRoles.has('match_manager');
  const isSuperAdmin = userRoles.has('super_admin');
  const isTeamAdmin = userRoles.has('team_admin');

  const normalizedUserId = useMemo(() => normalizeId(user), [user]);
  const managerScopeId = useMemo(() => normalizeId(routeManagerId) || normalizedUserId, [normalizedUserId, routeManagerId]);
  const organizationScope = routeOrganizationId || user?.organization;
  const organizationId = useMemo(() => getOrganizationId(organizationScope), [organizationScope]);
  const communityScope = routeCommunityId || user?.community;
  const communityId = useMemo(() => getCommunityId(communityScope), [communityScope]);
  const clubScope = routeClubId || user?.club;
  const clubId = useMemo(() => getClubId(clubScope), [clubScope]);

  const canView =
    isSuperAdmin ||
    isOrganizationAdmin ||
    isClubAdmin ||
    isTournamentAdmin ||
    isMatchManager ||
    (isTeamAdmin && managerScopeId === normalizedUserId);

  const tournamentFromState = useMemo(
    () => getKnownTournamentId({ params: {}, location }),
    [location],
  );

  const managementScope = useMemo(
    () =>
      buildManagementScope({
        organizationId,
        communityId,
        clubId,
        entityContext: activeContext,
      }),
    [activeContext, clubId, communityId, organizationId],
  );

  useEffect(() => {
    if (!managerScopeId || !tournamentFromState) return;
    const redirectPath = buildMatchManagerFormatsPath(managementScope, managerScopeId, tournamentFromState);
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [managementScope, managerScopeId, navigate, tournamentFromState]);

  useEffect(() => {
    const loadTournaments = async () => {
      if (!token || !canView) return;

      setIsLoading(true);
      setLoadError('');

      try {
        const response = await fetchTournaments(token, organizationScope, { communityId, clubId });
        setTournaments(response.tournaments || []);
      } catch (error) {
        setLoadError(error.message || 'Unable to load tournaments.');
      } finally {
        setIsLoading(false);
      }
    };

    loadTournaments();
  }, [canView, clubId, communityId, organizationScope, token]);

  const assignedTournaments = useMemo(() => {
    if (!Array.isArray(tournaments) || !managerScopeId) return [];

    return tournaments.filter((tournament) => {
      const matchManagerId = normalizeId(tournament?.matchManager) || normalizeId(tournament?.matchManagerId) || normalizeId(tournament?.assignedMatchManager);
      return matchManagerId && matchManagerId === managerScopeId;
    });
  }, [managerScopeId, tournaments]);

  useEffect(() => {
    if (isLoading || loadError || assignedTournaments.length === 0 || !managerScopeId || typeof window === 'undefined') {
      return;
    }

    const preferredTournamentId = normalizeId(window.localStorage.getItem(storedTournamentKey));
    if (!preferredTournamentId) return;

    const preferredTournament = assignedTournaments.find(
      (tournament) => normalizeId(getTournamentId(tournament)) === preferredTournamentId
    );

    if (!preferredTournament) {
      window.localStorage.removeItem(storedTournamentKey);
    }
  }, [assignedTournaments, isLoading, loadError, managerScopeId]);

  const handleTournamentClick = (tournamentId) => {
    if (!managerScopeId || !tournamentId) return;

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(storedTournamentKey, normalizeId(tournamentId));
    }

    const matchPath = buildMatchManagerFormatsPath(managementScope, managerScopeId, tournamentId);
    if (matchPath) {
      navigate(matchPath, { replace: false });
    }
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!canView) {
    return (
      <PageShell containerClassName="p-0">
        <div className="alert alert-warning mb-0">Not authorized to schedule matches.</div>
      </PageShell>
    );
  }

  const matchManagerPath =
    (communityId
      ? getCommunityMatchManagerDashboardPath(communityId, managerScopeId)
      : clubId
        ? getClubMatchManagerDashboardPath(clubId, managerScopeId)
        : getMatchManagerDashboardPath(organizationId, managerScopeId)) ||
    (communityId && managerScopeId
      ? `/community/${communityId}/match-manager/${managerScopeId}/tournaments`
      : clubId && managerScopeId
        ? `/club/${clubId}/match-manager/${managerScopeId}/tournaments`
        : organizationId && managerScopeId
          ? `/org/${organizationId}/match-manager/${managerScopeId}/tournaments`
          : '/');

  return (
    <PageShell containerClassName="p-0">
      <div className="py-4 d-grid gap-3">
        <div className="d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center gap-2">
          <div>
            <p className="eyebrow text-primary mb-1">Match manager</p>
            <h1 className="h4 mb-2">Assigned tournaments</h1>
            <p className="text-muted mb-0">Choose a tournament to open its match scheduling workspace.</p>
          </div>
          <div>
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/management')}>
              Back to Manager workspace
            </button>
          </div>
        </div>

        {loadError && <div className="alert alert-danger mb-0">{loadError}</div>}
        {isLoading && <div className="alert alert-secondary mb-0">Loading tournaments...</div>}

        {!isLoading && assignedTournaments.length === 0 && (
          <div className="alert alert-info mb-0">No tournaments are assigned to you yet.</div>
        )}

        {!isLoading &&
          assignedTournaments.map((tournament) => {
            const tournamentId = getTournamentId(tournament);
            return (
              <TournamentCard
                key={tournamentId}
                tournament={tournament}
                onClick={() => handleTournamentClick(tournamentId)}
                onManage={() => handleTournamentClick(tournamentId)}
                showActions
              />
            );
          })}

        {!isLoading && assignedTournaments.length > 0 && (
          <div className="text-muted small">
            Access link: <span className="text-nowrap">{matchManagerPath}</span>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default MatchManagerTournamentsPage;
